import React, { useMemo, useState } from 'react';
import {
  Button,
  InfoGroup,
  InfoItem,
  InfoItemKey,
  InfoItemValue,
  Box,
  Text,
  Link,
  Heading,
  CheckCircleIcon,
  EditIcon,
  StepItemIcon,
  Spinner,
  useToast,
  Badge,
  AlertOnlyIcon,
} from '@razorpay/blade/components';
import { useHistory } from 'react-router-dom';
import { useMutation } from 'react-query';
import api from 'api';
import { useFormWithProvider } from 'forms';
import { routePaths } from 'components/Routes/data';
import { FormFields } from 'components/EmployeeOnboarding/EmployeeSelfOnboarding/types';
import { OnboardingCompleted } from 'components/EmployeeOnboarding/EmployeeSelfOnboarding/components/OnboardingCompleted';
import { useGetBankAccountDetails } from 'components/EmployeeDetails/BankAccountDetails/hooks/useGetBankAccountDetails';
import { parseGraphqlErrorFromExtensions } from 'utils/Api';
import { AppError } from 'utils/AppError';
import { dateFormats, getFormattedDateValue } from 'utils/Dates';
import { getGenderTitle, getStateName, reviewDetailsValidationSchema } from './utils';
import { useSavedEmployeeData } from './hooks/useSavedEmployeeData';
import { useOptionalFields } from './hooks/useOptionalFields';
import {
  FormHeading,
  StyledForm,
  FormFooterWrapper,
  FormFieldsWrapper,
  BorderedBox,
} from 'components/EmployeeOnboarding/components/styles';
import { onboardingErrorMessages } from '../utils';
import useFeature from 'hooks/useFeature';
import { useOfficeLocationById } from '../CreateEmployee/hooks/useOfficeLocationById';

interface DetailItem {
  label: string;
  value?: string | null;
  shouldShowRequiredBadge?: boolean;
  shouldHide?: boolean;
}

interface Section {
  title: string;
  route: string;
  items: DetailItem[];
  canEdit?: boolean;
  shouldHide?: boolean;
}

export const ReviewDetailsInfoValue = ({ detailItem }: { detailItem: DetailItem }) => {
  if (detailItem.shouldHide) {
    return null;
  }

  if (detailItem.shouldShowRequiredBadge) {
    return (
      <InfoItem key={detailItem.label}>
        <InfoItemKey leading={() => <StepItemIcon icon={AlertOnlyIcon} color="notice" />}>
          {detailItem.label}
        </InfoItemKey>
        <InfoItemValue>
          <Badge color="negative">Required</Badge>
        </InfoItemValue>
      </InfoItem>
    );
  }

  return (
    <InfoItem key={detailItem.label}>
      <InfoItemKey leading={() => <StepItemIcon icon={CheckCircleIcon} color="positive" />}>
        {detailItem.label}
      </InfoItemKey>
      <InfoItemValue>
        <Text weight="regular">{detailItem.value || '-'}</Text>
      </InfoItemValue>
    </InfoItem>
  );
};

export const ReviewDetails = (): React.ReactElement => {
  const [showSocialMediaModal, setShowSocialMediaModal] = useState(false);
  const history = useHistory();
  const toast = useToast();
  const { formData, validate, errors, setFieldValue } = useFormWithProvider<FormFields>();
  const {
    isLoadingQuery,
    employeeDetails: { basicDetails, complianceDetails },
    shouldShowStatutoryDetailsStep,
  } = useSavedEmployeeData();

  const {
    latestBankAccount,
    queryResponse: { isLoading, isFetching },
  } = useGetBankAccountDetails();

  const { isEsicMultiLocationEnabled } = useFeature('esicMultiLocation');

  const { data: selectedOfficeLocation, isFetchingOfficeLocation } = useOfficeLocationById(
    basicDetails?.officeLocationId,
  );

  const { optionalFieldsKeys, optionalFields } = useOptionalFields();

  const isFormValid = useMemo(() => validate(reviewDetailsValidationSchema), [validate]);

  const sections: Section[] = [
    {
      title: 'Basic Details',
      route: routePaths.employeeOnboarding.employeeSelfOnboarding.personalDetails,
      canEdit: false,
      items: [
        { label: 'Name', value: basicDetails?.name },
        { label: 'Email ID', value: basicDetails?.email },
        { label: 'Joining Date', value: basicDetails?.hireDate },
        { label: 'Designation', value: basicDetails?.designation },
        { label: 'Department', value: basicDetails?.department },
        { label: 'Manager', value: basicDetails?.managerName },
        {
          label: 'Location',
          value: isEsicMultiLocationEnabled
            ? selectedOfficeLocation?.office_name
            : getStateName(basicDetails?.location),
        },
      ],
    },
    {
      title: 'KYC Information',
      route: routePaths.employeeOnboarding.employeeSelfOnboarding.kycDetails,
      canEdit: true,
      items: [
        { label: 'PAN', value: formData.pan, shouldShowRequiredBadge: !!errors.pan },
        {
          label: 'Aadhar Number',
          value: formData.aadharNumber,
          shouldShowRequiredBadge: !!errors.aadharNumber,
        },
      ],
    },
    {
      title: 'Bank Account Details',
      route: routePaths.employeeOnboarding.employeeSelfOnboarding.bankDetails,
      canEdit: true,
      items: [
        {
          label: 'Bank Account Number',
          value: latestBankAccount?.account_number,
          shouldShowRequiredBadge: !latestBankAccount?.account_number,
        },
        { label: 'Bank IFSC Code', value: latestBankAccount?.ifsc },
        { label: 'Beneficiary', value: latestBankAccount?.beneficiary_name },
      ],
    },
    {
      title: 'Statutory Information',
      route: routePaths.employeeOnboarding.employeeSelfOnboarding.statutoryDetails,
      canEdit: true,
      items: [
        {
          label: 'PF UAN',
          value: formData.doNotHavePfUan ? 'Not Available' : formData.pfUan,
          shouldShowRequiredBadge: !!errors.pfUan,
          shouldHide: !complianceDetails.isPfEnabled,
        },
        {
          label: 'ESIC IP Number',
          value: formData.doNotHaveEsicIpNumber ? 'Not Available' : formData.esicIpNumber,
          shouldShowRequiredBadge: !!errors.esicIpNumber,
          shouldHide: !complianceDetails.isEsicEnabled,
        },
      ],
      shouldHide: !shouldShowStatutoryDetailsStep,
    },
    {
      title: 'Personal Details',
      route: routePaths.employeeOnboarding.employeeSelfOnboarding.personalDetails,
      canEdit: true,
      items: [
        {
          label: 'Phone Number',
          value: formData.phoneNumber,
          shouldShowRequiredBadge: !!errors.phoneNumber,
        },
        {
          label: 'WhatsApp Integration',
          value: formData.enableWhatsapp ? 'Enabled' : 'Disabled',
        },
        { label: 'Gender', value: getGenderTitle(formData.gender?.[0]) },
        {
          label: 'Date of Birth',
          value: getFormattedDateValue({
            date: formData.dob,
            formatString: dateFormats.humanDateMonthYear,
            filler: '-',
          }),
          shouldShowRequiredBadge: !!errors.dob,
        },
        ...optionalFieldsKeys
          .filter((key) => !!formData[key])
          .map((key) => ({
            label: optionalFields[key].label,
            value:
              key === 'marriageAnniversary'
                ? getFormattedDateValue({
                    date: formData[key],
                    formatString: dateFormats.humanDateMonthYear,
                    filler: '-',
                  })
                : key === 'maritalStatus'
                ? formData[key]?.[0]
                : String(formData[key]),
          })),
      ],
    },
  ];

  const mutateEmployeeSelfOnboarding = useMutation({
    mutationFn: api.employeeOnboardingGql.mutations.selfOnboarding,
    onSuccess: () => {
      setShowSocialMediaModal(true);
    },
    onError: (error: typeof AppError) => {
      const errorMessage = parseGraphqlErrorFromExtensions(error);
      if (errorMessage === onboardingErrorMessages.panExists) {
        setFieldValue('panVerificationStatus', false);
      }
      toast.show({
        content: errorMessage,
        color: 'negative',
      });
    },
  });

  const handleBack = () => {
    history.push(routePaths.employeeOnboarding.employeeSelfOnboarding.personalDetails);
  };

  const handleContinue = () => {
    mutateEmployeeSelfOnboarding.mutate({
      pan: formData.pan,
      aadhaar: formData.aadharNumber,
      pfUan: formData.pfUan,
      esicIp: formData.esicIpNumber,
      phoneNumber: formData.phoneNumber,
      gender: formData.gender?.[0],
      dob: getFormattedDateValue({
        date: formData.dob,
        formatString: dateFormats.yearMonthDate,
        filler: '',
      }),
      enableWhatsapp: formData.enableWhatsapp ?? false,
      optionalEmployeeData: {
        ...optionalFieldsKeys.reduce((acc, key) => {
          if (key === 'marriageAnniversary') {
            acc[key] = getFormattedDateValue({
              date: formData[key],
              formatString: dateFormats.yearMonthDate,
              filler: '',
            });
          } else if (key === 'maritalStatus') {
            acc[key] = formData[key]?.[0] || '';
          } else {
            acc[key] = formData[key] || '';
          }
          return acc;
        }, {} as Record<string, string>),
      },
    });
  };

  const handleEditClick = (route: string) => {
    history.push(route);
  };

  const isLoadingDataQueries =
    isLoadingQuery || isLoading || isFetching || isFetchingOfficeLocation;

  return (
    <>
      <StyledForm>
        <FormFieldsWrapper>
          <FormHeading
            title="Review Details"
            description="Review all information carefully before finalising your profile."
          />

          <Box display="flex" flexDirection="column" gap="spacing.7">
            {isLoadingDataQueries ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Spinner accessibilityLabel="loading" />
              </Box>
            ) : (
              sections.filter((section) => !section.shouldHide).map((section) => (
                <BorderedBox key={section.title}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Heading size="medium" color="surface.text.gray.normal" weight="medium">
                      {section.title}
                    </Heading>
                    {section.canEdit && (
                      <Link
                        variant="button"
                        icon={EditIcon}
                        iconPosition="left"
                        onClick={() => handleEditClick(section.route)}>
                        Edit
                      </Link>
                    )}
                  </Box>

                  <InfoGroup
                    itemOrientation="horizontal"
                    size="medium"
                    valueAlign="right"
                    maxWidth="100%">
                    {section.items.map((item) => (
                      <ReviewDetailsInfoValue key={item.label} detailItem={item} />
                    ))}
                  </InfoGroup>
                </BorderedBox>
              ))
            )}
          </Box>
        </FormFieldsWrapper>

        <FormFooterWrapper>
          <Button
            variant="tertiary"
            size="medium"
            onClick={handleBack}
            isDisabled={mutateEmployeeSelfOnboarding.isLoading}>
            Back
          </Button>
          <Button
            variant="primary"
            size="medium"
            onClick={handleContinue}
            isDisabled={isLoadingDataQueries || !isFormValid}
            isLoading={mutateEmployeeSelfOnboarding.isLoading}>
            Continue
          </Button>
        </FormFooterWrapper>
      </StyledForm>

      <OnboardingCompleted
        isOpen={showSocialMediaModal}
        onDismiss={() => setShowSocialMediaModal(false)}
        designation={basicDetails?.designation}
      />
    </>
  );
};
